import type { ClientEntitiesSelectedOptions, InputUploads } from '@psw/shared';
import type { Location } from 'history';
import { immerable } from 'immer';
import {
  computeClientEntitiesAttributes,
  extractClientEntityFromCriterias,
} from '../Components/ProductFilters/ClientEntities/ClientyEntityFilters.helpers';
import { parseUrlQuery, stringifyToQueryUrl, UrlKeys } from '../Components/Utils/UrlUtil';
import { fetchTaxonsMonths } from '../Queries/taxon.queries';
import type { Catalog } from './Catalog';
import { Filter, type IFilterFormData } from './Filter';
import { TaxonMonth } from './TaxonMonth.model';

export class Filters {
  // To make it works with immer and produce
  [immerable] = true;

  productFilters: Filter;
  swapFilters: Filter;

  constructor() {
    this.productFilters = new Filter();
    this.swapFilters = new Filter();
  }

  equals(other: Filters) {
    return this.productFilters.equals(other.productFilters) && this.swapFilters.equals(other.swapFilters);
  }

  isEmpty() {
    const hasSuppliersOnProductFiltersOnly = this.productFilters.suppliers?.length > 0;
    return this.productFilters.isEmpty() || (this.swapFilters.isEmpty() && !hasSuppliersOnProductFiltersOnly);
  }

  checkIfVolumeUpdated(catalog: Catalog) {
    try {
      const hasDateRangeChanged = this.productFilters.hasDateRangeChanged || this.swapFilters.hasDateRangeChanged;
      const LeftClientEntities: ClientEntitiesSelectedOptions = extractClientEntityFromCriterias(
        this.productFilters.criterias,
        catalog?.client_entity_filters!,
      );
      const RightClientEntities: ClientEntitiesSelectedOptions = extractClientEntityFromCriterias(
        this.swapFilters.criterias,
        catalog?.client_entity_filters!,
      );
      const hasClientEntitiesLeft = Object.entries(LeftClientEntities).some(([key, values]) => values?.length);
      const hasClientEntitiesRight = Object.entries(RightClientEntities).some(([key, values]) => values?.length);
      return hasDateRangeChanged || hasClientEntitiesLeft || hasClientEntitiesRight;
    } catch (error) {
      return true;
    }
  }

  toJSON() {
    return {
      productFilters: this.productFilters.toJSON(),
      swapFilters: this.swapFilters.toJSON(),
    };
  }

  buildUrlParams(): string {
    const urlQuery: Record<string, string | number | string[] | number[]> = {};
    Object.keys(this)
      .filter(key => key === 'productFilters' || key === 'swapFilters')
      .forEach((key, i) => {
        const filter = this[key as keyof Filters] as Filter;
        // Categories
        if (filter.categories.length > 0) {
          urlQuery[`${UrlKeys.Categories}${i}`] = filter.categories[0];
        }

        // Criterias
        const criteriasKeys = Object.keys(filter.criterias);
        if (criteriasKeys.length > 0) {
          criteriasKeys.forEach(key => {
            urlQuery[`crit${i}.${key}`] = filter.criterias[key];
          });
        }

        // Suppliers
        // If some suppliers are selected, set them in the criterias.
        // Why ? Because initially, suppliers filters were displayed in the "Other Criterias" section on the left filters panel, with all others criterias.
        // But as it is an important filter for users, we decided to move it to the top of the left filters panel to make it more prominent.
        // To avoid changing the logic in the Arango queries, we keep the suppliers in the criterias.
        if (filter.suppliers.length > 0) {
          urlQuery[`crit${i}.Attributes/supplier_name`] = filter.suppliers;
        }

        // Date range
        // We update the urlQuery params only for the product filters, because swap filters does not have a date range
        if (
          key === 'productFilters' &&
          !!filter.dateRange[0] &&
          filter.dateRange[0] !== 0 &&
          !!filter.dateRange[1] &&
          filter.dateRange[1] !== 0
        ) {
          urlQuery[UrlKeys.DateRange] = filter.dateRange.map(date => date.toString());
        }
      });

    return stringifyToQueryUrl(urlQuery);
  }

  static fromFormValue(formValue: IFiltersFormData, catalog: Catalog) {
    const leftFilters = new Filters();
    leftFilters.productFilters = Filter.fromFormValue(formValue.productFilters, catalog);
    leftFilters.swapFilters = Filter.fromFormValue(formValue.swapFilters, catalog);
    return leftFilters;
  }

  /**
   * Build a Filters instance from a url
   * @param location
   * @param catalog
   */
  static async buildFromUrl(location: Location, catalog: Catalog): Promise<Filters> {
    const urlParsed = parseUrlQuery(location);
    const newFilters = new Filters();
    let dateRange = (urlParsed[UrlKeys.DateRange] as string[]) && [
      +(urlParsed[UrlKeys.DateRange] as string[])[0],
      +(urlParsed[UrlKeys.DateRange] as string[])[1],
    ];

    // We can just take the suppliers of the filters for the initial product (the one against which we will try to find swap alternatives).
    // Because on the swaps page of the SKU Level, we can only filter by suppliers of the initial product.
    // We can do this because those suppliers will be used to fetch the possible taxons, which are the same for both filters (left filters of products and left filters of swaps).
    // We can also do this because we do not support yet the possibility to get swaps for a product with a N-1 taxonomy (ie. a bigger one).
    //const suppliersOfInitialProduct = urlParsed['crit0.Attributes/supplier_name'] as string[] || [];

    // Fetch all possible months
    const taxonsMonths = await fetchTaxonsMonths();

    dateRange = dateRange || taxonsMonths.lastYearRange;

    for (const key of Object.keys(newFilters).filter(key => key === 'productFilters' || key === 'swapFilters')) {
      const filter = newFilters[key as keyof Filters] as Filter;
      const i = key === 'productFilters' ? 0 : 1;

      // Categories
      const taxonKey = (urlParsed[`${UrlKeys.Categories}${i}`] as string) || '';
      filter.categories = taxonKey ? [taxonKey] : [];

      // Suppliers
      filter.suppliers = (urlParsed[`crit${i}.Attributes/supplier_name`] as string[]) || [];

      // Criterias
      const urlParsedCriterias = Object.entries(urlParsed)
        .filter(([key, _]) => key.startsWith(`crit${i}.`))
        .map(([key, value]) => [key.replace(`crit${i}.`, ''), value]);
      const criterias: { [key: string]: string[] } = {};
      urlParsedCriterias.forEach(([key, value]) => {
        criterias[key as string] = value as string[];
      });

      // Date range
      filter.dateRange = dateRange;
      filter.hasDateRangeChanged = !TaxonMonth.isRangeLastYear(dateRange, taxonsMonths);

      // Client Entities
      const clientEntities: ClientEntitiesSelectedOptions = extractClientEntityFromCriterias(
        criterias,
        catalog?.client_entity_filters!,
      );
      filter.clientEntities = clientEntities;

      // Next step should only convert client entities values to string, "Attributes/storage_location_id": [2931] => ["2931"]
      const formatedClientEntitiesAttributes = computeClientEntitiesAttributes(
        clientEntities,
        catalog?.client_entity_filters!,
      );
      filter.criterias = { ...criterias, ...formatedClientEntitiesAttributes };
    }

    return newFilters;
  }
}

export interface IFiltersFormData {
  productFilters: IFilterFormData;
  swapFilters: IFilterFormData;
  inputUploads?: InputUploads;
}
